import { useToast } from '@quadminds/mindkit';
import { MindyDocRoles, MindyUsersPermissions } from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto, IUpdateOrganizationUserPayload, IUserOutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteOrganizationUser } from './useDeleteOrganizationUser';
import { useGetOrganizationInvites } from './useGetOrganizationInvites';
import { useOrganizationUserById } from './useGetOrganizationUserById';
import { useGetRoles } from './useGetRoles';
import { useIsKnowledge } from './useIsKnowledge';
import { useOrganizationUsers } from './useOrganizationUsers';
import { useRefreshToken } from './useRefreshToken';
import { useUpdateOrganizationUser } from './useUpdateOrganizationUser';
import {
    MINDY_DOC_ROLES,
    MINDY_PUBLIC_ROLES,
    checkUserAplications,
} from '../components/pages/Settings/UserConfig/UserConfig';
import { useByPassContext } from '../providers/ByPass/ByPass.provider';
import { agentsApi } from '../services/agents-api.service';
import { usersApi } from '../services/users.service';

export interface User {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    role: string;
    lastModifiedBy: string;
    lastModifiedDate: string;
    avatar: string;
    status?: 'active' | 'inactive' | 'pending' | 'error';
}

interface OrganizationMindySettings {
    mindy?: {
        licenses?: {
            max?: number | string;
        };
    };
}

const useUsersPage = () => {
    const [retry, setRetry] = useState<boolean>(true); // Estado para controlar el reintento de la solicitud
    const queryClient = useQueryClient(); // Access the query client
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']); // Retrieve userInfo from the cache
    const currentOrganizationId = userInfo?.currentOrganization || '';
    const [page, setPage] = useState(1); // Estado para la página actual
    const [limit] = useState(30); // Estado para el límite de usuarios por página
    const [searchQuery, setSearchQuery] = useState(''); // Estado para el texto de búsqueda
    const [statusFilter, setStatusFilter] = useState<string>(''); // Estado para el filtro de activo/inactivo
    const [roleFilter, setRoleFilter] = useState<string>(''); // Estado para el filtro de roles

    // --- isKnowledge states ---
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<IUserOutputDto | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [inviteEmails, setInviteEmails] = useState<string[]>([]);
    const [currentEmailInput, setCurrentEmailInput] = useState<string>('');
    const [resendCooldowns, setResendCooldowns] = useState<Record<string, number>>(() => {
        try {
            const stored = localStorage.getItem('resendCooldowns');
            if (!stored) return {};
            const parsed: Record<string, number> = JSON.parse(stored);
            const now = Date.now();
            // Filter out expired cooldowns
            return Object.fromEntries(Object.entries(parsed).filter(([, expiry]) => expiry > now));
        } catch {
            return {};
        }
    });
    const [invitedUsersMap, setInvitedUsersMap] = useState<Record<string, IUserOutputDto>>({});

    const { mutateAsync: triggerRefresh } = useRefreshToken();
    const { mutateAsync: updateOrganizationUser } = useUpdateOrganizationUser();
    const { mutateAsync: deleteOrganizationUser } = useDeleteOrganizationUser();
    const { t } = useTranslation();
    const { toast } = useToast();
    const { data: currentOrganizationUser } = useOrganizationUserById(currentOrganizationId, userInfo?.id || ''); // Hook para obtener los datos del usuario actual
    const { data: roles } = useGetRoles();
    const { data: invites, refetch: refetchInvites } = useGetOrganizationInvites(currentOrganizationId);

    const mindyApp = useMemo(() => {
        return currentOrganizationUser?.userOrganizations
            ?.find((organization) => organization.organization === currentOrganizationId)
            ?.applications?.find((application) => application.name === 'mindy');
    }, [currentOrganizationUser, currentOrganizationId]);

    const mindyAppRoles = useMemo(() => {
        if (!roles) return [];

        return roles
            .filter((role) => {
                return role.name && MINDY_PUBLIC_ROLES[role.name];
            })
            .map((role) => ({
                id: role.id,
                name: role.name,
                displayName: MINDY_PUBLIC_ROLES[role.name].displayName,
                translationKey: MINDY_PUBLIC_ROLES[role.name].translationKey,
                permissions: role.permissions,
            }));
    }, [roles]);

    const mindyDocRoles = useMemo(() => {
        if (!roles) return [];

        return roles
            .filter((role) => role.name && MINDY_DOC_ROLES[role.name])
            .map((role) => ({
                id: role.id,
                name: role.name,
                displayName: MINDY_DOC_ROLES[role.name].displayName,
                translationKey: MINDY_DOC_ROLES[role.name].translationKey,
                permissions: role.permissions,
            }));
    }, [roles]);

    const {
        data: response,
        isLoading,
        error,
        refetch,
    } = useOrganizationUsers(
        userInfo?.currentOrganization || '',
        page,
        limit,
        searchQuery,
        statusFilter,
        roleFilter,
        mindyApp?.id,
    );

    const { data: allUsersResponse } = useOrganizationUsers(
        userInfo?.currentOrganization || '',
        1,
        1,
        undefined,
        undefined,
        undefined,
        mindyApp?.id,
    );
    const currentUserPermissions = userInfo?.permissions || [];
    const { isImpersonated } = useByPassContext();

    const users = response?.data || [];
    const totalPages = response?.meta?.totalPages || 0;

    const [selectedConfigUser, setSelectedConfigUser] = useState('');

    const currentOrganization = userInfo?.organizations?.find((org) => org.id === currentOrganizationId);
    const maxLicensesFromSettings = (currentOrganization?.settings as OrganizationMindySettings | undefined)?.mindy
        ?.licenses?.max;
    const maxLicenses =
        typeof maxLicensesFromSettings === 'number' ? maxLicensesFromSettings : Number(maxLicensesFromSettings) || 10;

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(1);
    };

    const handleToggleActive = async (userId: string, isActive: boolean) => {
        const userToEdit = users.find((u) => u.id === userId) as IUserOutputDto | undefined;

        if (!userToEdit || !mindyApp?.id) {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
            return;
        }

        const currentUserOrganization = userToEdit.userOrganizations?.find(
            (organization) => organization.organization === currentOrganizationId,
        );

        if (!currentUserOrganization) {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
            return;
        }

        const wasActive = checkUserAplications(userToEdit, currentOrganizationId);
        let applicationsIds: string[] =
            currentUserOrganization.applications?.map((application) => application.id) || [];

        if (isActive && !wasActive) {
            applicationsIds = [...applicationsIds, mindyApp.id];
        } else if (!isActive && wasActive) {
            applicationsIds = applicationsIds.filter(
                (applicationId) =>
                    !currentUserOrganization.applications?.find(
                        (organizationApplication) =>
                            organizationApplication.id === applicationId && organizationApplication.name === 'mindy',
                    ),
            );
        }

        const updatePayload: IUpdateOrganizationUserPayload = {
            applications: applicationsIds,
        };

        try {
            await updateOrganizationUser({
                organizationId: currentOrganizationId,
                userId,
                payload: updatePayload,
            });

            if (isActive && !wasActive) {
                await agentsApi.addOrganizationUsersBulk(currentOrganizationId, [
                    { user_id: userId, email: userToEdit.email },
                ]);
            } else if (!isActive && wasActive) {
                await agentsApi.deleteOrganizationUsersBulk(currentOrganizationId, [{ user_id: userId }]);
            }

            await queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
            await queryClient.invalidateQueries({ queryKey: ['organizationUserById'] });

            toast({
                title: isActive
                    ? t('settings.user.userActivated', 'Usuario activado')
                    : t('settings.user.userDeactivated', 'Usuario desactivado'),
                description: t('settings.user.successDescription', 'Los cambios se han guardado correctamente'),
            });
        } catch {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
        }
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleStatusChange = (value: string) => {
        setStatusFilter(value);
        setPage(1);
    };

    const handleRoleChange = (value: string) => {
        setRoleFilter(value);
        setPage(1);
    };

    // --- isKnowledge flag ---
    const { isKnowledge } = useIsKnowledge();

    // --- pending users from API invites ---
    const existingUserEmails = new Set([...Object.keys(invitedUsersMap), ...users.map((u) => u.email).filter(Boolean)]);
    const pendingUsers = (invites || [])
        .filter((invite) => !existingUserEmails.has(invite.email))
        .map((invite) => {
            return {
                id: invite.id,
                email: invite.email,
                firstName: '',
                lastName: '',
                status: (invite.redeemed ? 'redeemed' : 'pending') as 'redeemed' | 'pending',
                avatar: '',
            };
        });

    // --- isKnowledge computed values ---
    const allUsersIncludingPending = [...users, ...pendingUsers];
    const totalUsersCount = allUsersResponse?.meta?.total ?? 0;
    const usedSlots = totalUsersCount + pendingUsers.length;
    const availableSlots = maxLicenses - usedSlots;

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddEmail = (email: string) => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) return;

        if (!validateEmail(trimmedEmail)) {
            toast({
                title: 'Email inválido',
                description: 'Por favor ingresá un email válido',
                variant: 'destructive',
            });
            return;
        }

        if (inviteEmails.includes(trimmedEmail)) {
            toast({
                title: 'Email duplicado',
                description: 'Este email ya fue agregado',
                variant: 'destructive',
            });
            return;
        }

        const currentAvailableSlots = availableSlots - inviteEmails.length;
        if (currentAvailableSlots <= 0) {
            toast({
                title: 'Límite alcanzado',
                description: 'No hay más usuarios disponibles',
                variant: 'destructive',
            });
            return;
        }

        setInviteEmails([...inviteEmails, trimmedEmail]);
        setCurrentEmailInput('');
    };

    const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ',' || e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
            const email = currentEmailInput.trim();
            if (email) {
                handleAddEmail(email);
            }
        } else if (e.key === 'Backspace' && !currentEmailInput && inviteEmails.length > 0) {
            setInviteEmails(inviteEmails.slice(0, -1));
        }
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        setInviteEmails(inviteEmails.filter((e) => e !== emailToRemove));
    };

    const handleInviteUsers = async () => {
        if (inviteEmails.length === 0) {
            toast({
                title: 'Error',
                description: 'Agregá al menos un email',
                variant: 'destructive',
            });
            return;
        }

        // Find the consultant role ID from fetched roles
        const consultantRole = roles?.find((role) => role.name === 'mindy:consultant-doc');

        try {
            await usersApi.sendInvite(currentOrganizationId, {
                email: inviteEmails,
                typology: 'MINDY_CLARO_KNOWLEDGE_USER',
                notificationType: 'MINDY_KNOWLEDGE_USER_INVITATION',
                application: 'mindy',
                roles: consultantRole ? [consultantRole.id] : [],
            });
            setInviteEmails([]);
            setCurrentEmailInput('');
            setIsInviteModalOpen(false);
            refetch();
            refetchInvites();
            toast({
                title: t('settings.user.inviteSent', 'Invitaciones enviadas'),
                description: `Se enviaron ${inviteEmails.length} invitación(es)`,
            });
        } catch {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
        }
    };

    const handleEditUser = () => {
        toast({
            title: 'Usuario actualizado',
            description: 'Los cambios se han guardado correctamente',
        });
        setIsEditModalOpen(false);
        setSelectedUser(null);
    };

    const handleDeleteUser = async () => {
        if (!selectedUser?.id) return;

        try {
            await deleteOrganizationUser({
                organizationId: currentOrganizationId,
                userId: selectedUser.id,
            });

            await queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
            await queryClient.invalidateQueries({ queryKey: ['organizationUserById'] });

            toast({
                title: 'Usuario eliminado',
                description: 'El usuario ha sido eliminado correctamente',
            });
            setIsDeleteModalOpen(false);
            setSelectedUser(null);
        } catch {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
        }
    };

    const handleSelectUser = (userId: string, checked: boolean) => {
        if (checked) {
            setSelectedUsers([...selectedUsers, userId]);
        } else {
            setSelectedUsers(selectedUsers.filter((id) => id !== userId));
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = users.filter((u) => u.id !== userInfo?.id).map((u) => u.id);
            setSelectedUsers(allIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleDeactivateSelected = () => {
        toast({
            title: 'Usuarios desactivados',
            description: `Se desactivaron ${selectedUsers.length} usuario(s)`,
        });
        setSelectedUsers([]);
    };

    const handleCancelInvite = async (inviteId: string) => {
        try {
            await usersApi.deleteInvite(currentOrganizationId, inviteId);
            refetchInvites();
            toast({
                title: t('settings.user.invitationCanceled', 'Invitación cancelada'),
            });
        } catch {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
        }
    };

    const handleResendInvitation = async (email: string) => {
        if (resendCooldowns[email] && Date.now() < resendCooldowns[email]) {
            const secondsLeft = Math.ceil((resendCooldowns[email] - Date.now()) / 1000);
            toast({
                title: t('settings.user.resendCooldown', 'Esperá un momento'),
                description: `Podés reenviar en ${secondsLeft} segundos`,
                variant: 'destructive',
            });
            return;
        }

        const consultantRole = roles?.find((role) => role.name === MindyDocRoles.CONSULTANT_DOC);

        try {
            await usersApi.sendInvite(currentOrganizationId, {
                email: [email],
                typology: 'MINDY_CLARO_KNOWLEDGE_USER',
                notificationType: 'MINDY_KNOWLEDGE_USER_INVITATION',
                application: 'mindy',
                roles: consultantRole ? [consultantRole.id] : [],
            });
            setResendCooldowns((prev) => ({ ...prev, [email]: Date.now() + 60000 }));
            toast({
                title: t('settings.user.inviteSent', 'Invitación reenviada'),
                description: `Se reenvió la invitación a ${email}`,
            });
        } catch {
            toast({
                title: t('errors.genericTitle'),
                description: t('errors.generic'),
                variant: 'destructive',
            });
        }
    };

    const visiblePages = Array.from({ length: totalPages }, (_, index) => index + 1).filter(
        (pageNumber) =>
            pageNumber === 1 || pageNumber === totalPages || (pageNumber >= page - 2 && pageNumber <= page + 2),
    );

    const showActions =
        currentUserPermissions.includes(MindyUsersPermissions.UpdateUser) || isImpersonated || Boolean(isKnowledge);

    // Persist cooldowns to localStorage
    useEffect(() => {
        localStorage.setItem('resendCooldowns', JSON.stringify(resendCooldowns));
    }, [resendCooldowns]);

    // Search for users by invite emails
    useEffect(() => {
        const searchInvitedUsers = async () => {
            if (!invites || invites.length === 0) {
                setInvitedUsersMap({});
                return;
            }

            try {
                const inviteEmails = invites.map((invite) => invite.email);
                const filter = { email: { $in: inviteEmails } };

                const response = await usersApi.getOrganizationUsers(
                    currentOrganizationId,
                    1,
                    invites.length,
                    JSON.stringify(filter),
                );

                const usersByEmail: Record<string, IUserOutputDto> = {};
                if (response.data && Array.isArray(response.data)) {
                    response.data.forEach((user) => {
                        usersByEmail[user.email] = user;
                    });
                }
                setInvitedUsersMap(usersByEmail);
            } catch (error) {
                // Silent fail - if we can't find users, just show invites
                console.warn('Failed to fetch invited users:', error);
            }
        };

        searchInvitedUsers();
    }, [invites, currentOrganizationId]);

    const getResendCooldownSeconds = (email: string): number => {
        const expiry = resendCooldowns[email];
        if (!expiry || Date.now() >= expiry) return 0;
        return Math.ceil((expiry - Date.now()) / 1000);
    };

    useEffect(() => {
        if (!isLoading && (error as Error & { response?: { status: number } })?.response?.status === 403 && retry) {
            console.warn('403 Forbidden: Refreshing token');
            setRetry(false);
            triggerRefresh()
                .then(() => {
                    refetch();
                })
                .catch(() => {
                    toast({
                        title: t('errors.genericTitle'),
                        description: t('errors.generic'),
                        variant: 'destructive',
                    });
                });
        }
    }, [error, isLoading, refetch, retry, t, toast, triggerRefresh]);

    return {
        retry,
        setRetry,
        queryClient,
        userInfo,
        currentOrganizationId,
        page,
        setPage,
        limit,
        searchQuery,
        setSearchQuery,
        statusFilter,
        setStatusFilter,
        roleFilter,
        setRoleFilter,
        triggerRefresh,
        t,
        toast,
        currentOrganizationUser,
        roles,
        mindyApp,
        mindyAppRoles,
        mindyDocRoles,
        response,
        isLoading,
        error,
        refetch,
        currentUserPermissions,
        isImpersonated,
        users,
        totalPages,
        selectedConfigUser,
        setSelectedConfigUser,
        isInviteModalOpen,
        setIsInviteModalOpen,
        isEditModalOpen,
        setIsEditModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        selectedUser,
        setSelectedUser,
        selectedUsers,
        setSelectedUsers,
        inviteEmails,
        setInviteEmails,
        currentEmailInput,
        setCurrentEmailInput,
        pendingUsers,
        refetchInvites,
        maxLicenses,
        handlePageChange,
        handleSearchChange,
        handleToggleActive,
        handleStatusChange,
        handleRoleChange,
        isKnowledge,
        allUsersIncludingPending,
        totalUsersCount,
        usedSlots,
        availableSlots,
        validateEmail,
        checkUserAplications,
        handleAddEmail,
        handleEmailInputKeyDown,
        handleRemoveEmail,
        handleInviteUsers,
        handleEditUser,
        handleDeleteUser,
        handleSelectUser,
        handleSelectAll,
        handleDeactivateSelected,
        handleCancelInvite,
        handleResendInvitation,
        getResendCooldownSeconds,
        visiblePages,
        showActions,
    };
};

export default useUsersPage;
